import { Link } from "wouter";
import { SEO } from "@/components/SEO";
import { PageHero } from "@/components/ui/page-hero";
import { heroImages } from "@/lib/image-map";
import { pageSchema } from "@/lib/organization";
import { serviceAreaLinks } from "@/lib/site-navigation";
import { townServiceLinks } from "@/lib/site-navigation";
import { BeforeAfterSlider } from "@/components/ui/before-after-slider";
import { beforeAfterSets } from "@/lib/image-map";

export interface TownPageData {
  town: string;
  path: string;
  description: string;
  headline: string;
  preserveFranklinProject?: boolean;
}

const metaDescriptions: Record<string, string> = {
  Franklin: "Landscape maintenance, lawn care, and seasonal cleanups for Franklin, Massachusetts homes and businesses from TLC Associates LLC, based in Franklin.",
  Bellingham: "TLC Associates LLC serves Bellingham, Massachusetts with landscape maintenance, lawn care, tree and shrub care, and snow plowing.",
  Medway: "Landscape maintenance and lawn care in Medway, Massachusetts from TLC Associates LLC, including garden planting, stonework, and seasonal cleanups.",
  Norfolk: "TLC Associates LLC provides year-round landscape maintenance, lawn care, and snow removal for properties in Norfolk, Massachusetts.",
  Wrentham: "Landscape maintenance, lawn care, and landscaping services in Wrentham, Massachusetts from TLC Associates LLC, a Franklin-based contractor.",
};

export function TownPage({ town, path, description, headline, preserveFranklinProject = false }: TownPageData) {
  const title = `TLC Associates LLC | Landscape Maintenance Contractor | ${town}, Massachusetts`;
  const metaDescription = metaDescriptions[town];
  const otherTowns = serviceAreaLinks.filter((area) => area.name !== town);

  return (
    <>
      <SEO
        title={title}
        description={metaDescription}
        path={path}
        schema={pageSchema(path, "WebPage", title, metaDescription)}
      />
      <PageHero
        imageSrc={heroImages["/service-areas"]}
        eyebrow={`Landscape Contractor Serving ${town}, Massachusetts`}
        headline={headline}
        description={`Landscape services for properties in ${town}.`}
      />
      <section className="bg-[#f7f3ed] py-20">
        <div className="container mx-auto max-w-4xl px-4 md:px-6">
          <p className="text-xl leading-relaxed text-[#1a1a1a]">{description}</p>
          {preserveFranklinProject && (
            <div className="mt-12">
              <BeforeAfterSlider
                beforeImage={beforeAfterSets[10].before.large}
                afterImage={beforeAfterSets[10].after.large}
                beforeImageSmall={beforeAfterSets[10].before.small}
                afterImageSmall={beforeAfterSets[10].after.small}
                altText="Driveway edge clearing before and after in Franklin MA"
                caption="Driveway Edge Clearing"
              />
            </div>
          )}
        </div>
      </section>
      <section className="bg-white py-20">
        <div className="container mx-auto max-w-5xl px-4 md:px-6">
          <h2 className="mb-10 text-3xl text-[#1a3a1a]">Our Services</h2>
          <div className="grid gap-8 md:grid-cols-2">
            {townServiceLinks.map((service) => (
              <div key={service.path} className="rounded-xl bg-[#f7f3ed] p-7">
                <h3 className="mb-3 text-2xl text-[#1a3a1a]">
                  <Link href={service.path} className="hover:text-[#2a7a2a] hover:underline">
                    {service.name}
                  </Link>
                </h3>
                <p className="leading-relaxed text-gray-700">{service.summary}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="bg-[#f7f3ed] py-20">
        <div className="container mx-auto max-w-5xl px-4 md:px-6">
          <h2 className="mb-10 text-3xl text-[#1a3a1a]">{town} Service Areas</h2>
          <p className="mb-8 max-w-3xl text-lg leading-relaxed text-gray-700">
            TLC Associates LLC works throughout {town} and the surrounding Norfolk County communities, with service plans shaped around each property.
            Each estimate begins with a property walkthrough so the requested work, access, and seasonal priorities can be reviewed together.
          </p>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {otherTowns.map((area) => (
              <h3 key={area.path} className="rounded-lg bg-white p-5 text-xl text-[#1a3a1a] shadow-sm">
                <Link href={area.path} className="hover:text-[#2a7a2a] hover:underline">
                  {area.name}
                </Link>
              </h3>
            ))}
            <h3 className="rounded-lg bg-white p-5 text-xl text-[#1a3a1a] shadow-sm">
              <Link href="/" className="hover:text-[#2a7a2a] hover:underline">
                All of Norfolk County
              </Link>
            </h3>
          </div>
        </div>
      </section>
      <section className="bg-[#c84060] py-24">
        <div className="container mx-auto px-4 text-center">
          <h2 className="mb-6 text-3xl text-white md:text-4xl">Request an Estimate in {town}</h2>
          <p className="mx-auto mb-10 max-w-2xl text-lg text-white/90">
            Contact TLC Associates LLC for a walkthrough and a detailed estimate for your {town} property.
          </p>
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link href="/contact#estimate-form" className="rounded-lg bg-white px-8 py-4 text-lg font-medium text-[#1a3a1a] hover:bg-gray-100">
              Request Your Free Estimate
            </Link>
            <a href="tel:5085051539" className="px-6 py-3 text-lg font-medium text-white hover:underline">
              Or call 508-505-1539
            </a>
          </div>
        </div>
      </section>
    </>
  );
}